import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ApiError } from "@/lib/api";
import type { AdminPlaybookRow } from "@/queries/adminPlaybooks";
import {
  useCreateStrategy,
  useUpdateStrategy,
  type AdminStrategy,
  type CreateStrategyBody,
  type UpdateStrategyBody,
} from "@/queries/adminStrategies";

import { LiveEnableConfirmDialog } from "./LiveEnableConfirmDialog";

interface CommonProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  playbooks: AdminPlaybookRow[];
}

type Props =
  | (CommonProps & { mode: "create"; strategy?: undefined })
  | (CommonProps & { mode: "edit"; strategy: AdminStrategy });

interface FormState {
  name: string;
  description: string;
  playbookIds: Set<string>;
  dailyBudgetUsd: string;
  maxPositionSizePct: string;
  cooldownHours: string;
  liveConfidenceThreshold: string;
  liveEnabled: boolean;
}

function initialState(props: Props): FormState {
  if (props.mode === "edit") {
    return {
      name: props.strategy.name,
      description: props.strategy.description ?? "",
      playbookIds: new Set(props.strategy.playbook_ids),
      dailyBudgetUsd: props.strategy.daily_budget_usd,
      maxPositionSizePct: props.strategy.max_position_size_pct,
      cooldownHours: String(props.strategy.cooldown_after_losing_trade_hours),
      liveConfidenceThreshold: props.strategy.live_confidence_threshold,
      liveEnabled: props.strategy.live_enabled,
    };
  }
  return {
    name: "",
    description: "",
    playbookIds: new Set<string>(),
    dailyBudgetUsd: "500",
    maxPositionSizePct: "0.05",
    cooldownHours: "24",
    liveConfidenceThreshold: "0.65",
    liveEnabled: false,
  };
}

export function StrategyEditDialog(props: Props) {
  const { t } = useTranslation("admin-strategies");
  const { mode, playbooks, onOpenChange } = props;

  const [form, setForm] = useState<FormState>(() => initialState(props));
  const [error, setError] = useState<string | null>(null);
  const [liveConfirmOpen, setLiveConfirmOpen] = useState(false);

  const createMutation = useCreateStrategy();
  const updateMutation = useUpdateStrategy(
    mode === "edit" ? props.strategy.id : "",
  );
  const isPending = createMutation.isPending || updateMutation.isPending;

  const previousLiveEnabled = mode === "edit" ? props.strategy.live_enabled : false;

  const sortedPlaybooks = useMemo(
    () => [...playbooks].sort((a, b) => a.display_name.localeCompare(b.display_name)),
    [playbooks],
  );

  const togglePlaybook = (id: string) => {
    setForm((prev) => {
      const next = new Set(prev.playbookIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { ...prev, playbookIds: next };
    });
  };

  const handleSelectAll = () => {
    setForm((prev) => ({
      ...prev,
      playbookIds: new Set(sortedPlaybooks.map((pb) => pb.id)),
    }));
  };

  const handleClear = () => {
    setForm((prev) => ({ ...prev, playbookIds: new Set<string>() }));
  };

  const validate = (): string | null => {
    if (form.name.trim() === "") return t("errors.nameRequired");
    if (form.playbookIds.size === 0) return t("errors.atLeastOnePlaybook");
    const budget = Number(form.dailyBudgetUsd);
    if (!Number.isFinite(budget) || budget <= 0) {
      return t("errors.budgetMustBePositive");
    }
    const pos = Number(form.maxPositionSizePct);
    if (!Number.isFinite(pos) || pos <= 0 || pos > 1) {
      return t("errors.positionSizeRange");
    }
    const conf = Number(form.liveConfidenceThreshold);
    if (!Number.isFinite(conf) || conf < 0 || conf > 1) {
      return t("errors.confidenceRange");
    }
    return null;
  };

  const buildBody = (): CreateStrategyBody => ({
    name: form.name.trim(),
    description: form.description.trim() || null,
    playbook_ids: Array.from(form.playbookIds),
    daily_budget_usd: Number(form.dailyBudgetUsd),
    max_position_size_pct: Number(form.maxPositionSizePct),
    cooldown_after_losing_trade_hours: Math.max(0, Number(form.cooldownHours) || 0),
    live_confidence_threshold: Number(form.liveConfidenceThreshold),
    live_enabled: form.liveEnabled,
  });

  const submit = async () => {
    setError(null);
    const reason = validate();
    if (reason) {
      setError(reason);
      return;
    }
    try {
      if (mode === "create") {
        await createMutation.mutateAsync(buildBody());
      } else {
        const body: UpdateStrategyBody = buildBody();
        await updateMutation.mutateAsync(body);
      }
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError) {
        const detail = err.detail as { detail?: { code?: string } } | undefined;
        const code = detail?.detail?.code;
        if (code === "conflict") {
          setError(t("errors.nameTaken"));
        } else if (code === "live_gates_unmet") {
          setError(t("errors.liveGatesUnmet"));
        } else {
          setError(t("errors.saveGeneric"));
        }
      } else {
        setError(t("errors.saveGeneric"));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // If user is turning live ON (it was off previously), open the confirmation modal.
    if (form.liveEnabled && !previousLiveEnabled) {
      setError(null);
      setLiveConfirmOpen(true);
      return;
    }
    await submit();
  };

  const handleLiveConfirmed = async () => {
    setLiveConfirmOpen(false);
    await submit();
  };

  const title =
    mode === "create"
      ? t("edit.titleCreate")
      : t("edit.titleEdit", { name: props.strategy.name });

  return (
    <>
      <Dialog open={props.open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
            </DialogHeader>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1 sm:col-span-2">
                <Label htmlFor="strategy-name">{t("edit.name")}</Label>
                <Input
                  id="strategy-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={t("edit.namePlaceholder")}
                />
              </div>
              <div className="flex flex-col gap-1 sm:col-span-2">
                <Label htmlFor="strategy-description">{t("edit.description")}</Label>
                <Input
                  id="strategy-description"
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  placeholder={t("edit.descriptionPlaceholder")}
                />
              </div>

              <div className="flex flex-col gap-1">
                <Label htmlFor="strategy-budget">{t("edit.dailyBudgetUsd")}</Label>
                <Input
                  id="strategy-budget"
                  inputMode="decimal"
                  value={form.dailyBudgetUsd}
                  onChange={(e) =>
                    setForm({ ...form, dailyBudgetUsd: e.target.value })
                  }
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="strategy-position">
                  {t("edit.maxPositionSizePct")}
                </Label>
                <Input
                  id="strategy-position"
                  inputMode="decimal"
                  value={form.maxPositionSizePct}
                  onChange={(e) =>
                    setForm({ ...form, maxPositionSizePct: e.target.value })
                  }
                />
                <p className="text-xs text-muted-foreground">
                  {t("edit.maxPositionSizePctHint")}
                </p>
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="strategy-cooldown">{t("edit.cooldownHours")}</Label>
                <Input
                  id="strategy-cooldown"
                  inputMode="numeric"
                  value={form.cooldownHours}
                  onChange={(e) =>
                    setForm({ ...form, cooldownHours: e.target.value })
                  }
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="strategy-confidence">
                  {t("edit.liveConfidenceThreshold")}
                </Label>
                <Input
                  id="strategy-confidence"
                  inputMode="decimal"
                  value={form.liveConfidenceThreshold}
                  onChange={(e) =>
                    setForm({ ...form, liveConfidenceThreshold: e.target.value })
                  }
                />
                <p className="text-xs text-muted-foreground">
                  {t("edit.liveConfidenceThresholdHint")}
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-2 rounded-md border p-3">
              <div className="flex items-center justify-between">
                <Label>{t("edit.playbooks")}</Label>
                <div className="flex gap-2 text-xs">
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="text-primary hover:underline"
                  >
                    {t("edit.playbooksSelectAll")}
                  </button>
                  <span className="text-muted-foreground">·</span>
                  <button
                    type="button"
                    onClick={handleClear}
                    className="text-primary hover:underline"
                  >
                    {t("edit.playbooksClear")}
                  </button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">{t("edit.playbooksHint")}</p>
              <div className="flex max-h-48 flex-col gap-1 overflow-auto pr-1">
                {sortedPlaybooks.map((pb) => {
                  const checked = form.playbookIds.has(pb.id);
                  return (
                    <label
                      key={pb.id}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-accent"
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-input accent-primary"
                        checked={checked}
                        onChange={() => togglePlaybook(pb.id)}
                      />
                      <span className="flex-1 text-sm">{pb.display_name}</span>
                      <span className="text-xs text-muted-foreground">{pb.id}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center justify-between rounded-md border p-3">
              <div className="flex flex-col gap-0.5">
                <Label htmlFor="strategy-live">{t("edit.liveEnabled")}</Label>
                <p className="text-xs text-muted-foreground">{t("edit.liveEnabledHint")}</p>
              </div>
              <Switch
                id="strategy-live"
                checked={form.liveEnabled}
                onCheckedChange={(v) => setForm({ ...form, liveEnabled: v })}
              />
            </div>

            {error ? (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}

            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                {t("edit.cancel")}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? mode === "create"
                    ? t("edit.creating")
                    : t("edit.saving")
                  : mode === "create"
                    ? t("edit.create")
                    : t("edit.save")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {liveConfirmOpen ? (
        <LiveEnableConfirmDialog
          open
          onOpenChange={(next) => !next && setLiveConfirmOpen(false)}
          strategyName={form.name || t("liveConfirm.title")}
          onConfirmed={handleLiveConfirmed}
        />
      ) : null}
    </>
  );
}
